import {
  ChevronDownIcon,
  ChevronRightIcon,
  HashIcon,
  LogInIcon,
  LogOutIcon,
  MessagesSquareIcon,
  PlusIcon,
  RefreshCwIcon,
  Trash2Icon,
  UsersIcon,
} from 'lucide-react'
import { ApiErrorText } from '@/components/UpgradePrompt'
import { Fragment, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router'

import { errorMessage } from '@/api/client'
import {
  isMissingPresence,
  useChannelMembers,
  useChannels,
  useConversations,
  useCreateChannel,
  useDeleteChannel,
  useJoinChannel,
  useLeaveChannel,
  useMe,
  useMessageIndex,
  usePresence,
} from '@/api/queries'
import type { ChannelSummary, ZoneSummary } from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { MissingPresenceNotice } from '@/components/PresenceControl'
import { PausedZoneNotice, RequireZone } from '@/components/RequireZone'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute, formatRelative, pluralise } from '@/lib/format'
import { cn } from '@/lib/utils'
import { hiddenSenders, visibleMessages } from './agent-visibility'
import { visibleParticipants } from './visible-participants'
import { useReadOnly } from '@/staff/hooks'

/**
 * Members and conversations for one expanded channel row.
 *
 * Members are shown AS THEY ARE — never filtered by agent visibility, for the same reason the
 * channels list itself is not (§ spec): hiding an agent must not make a channel's roster look
 * emptier than it is.
 *
 * Conversations go through the shared per-zone filter in TWO places, for two different reasons:
 *
 * - Each segment's own `participants` (via `visibleParticipants`) — kept for correctness if the
 *   backend ever populates that field for a CHANNEL segment, which it does not today (see
 *   `visible-participants.ts`'s javadoc: `ConversationAssembler` hands back `List.of()` for every
 *   channel segment, verified live against the dev zone).
 * - The **Speakers** line, derived from `GET /messages/index?channel=` (`useMessageIndex`) — this
 *   is what actually realises "an agent hidden on the Messages page must not reappear because a
 *   channel's conversation view was opened" against real data today, since the field above never
 *   has anything in it to filter.
 */
function ChannelDetail({
  zone,
  channel,
  myName,
  hidden,
}: {
  zone: ZoneSummary
  channel: ChannelSummary
  myName: string | undefined
  hidden: string[]
}) {
  const readOnly = useReadOnly()
  const members = useChannelMembers(zone.id, channel.id)
  const conversations = useConversations(zone.id, false, channel.id)
  const index = useMessageIndex(zone.id, channel.id)
  const joinChannel = useJoinChannel()
  const leaveChannel = useLeaveChannel()

  const memberRows = members.data ?? []
  const conversationRows = conversations.data?.conversations ?? []
  const isMember = myName !== undefined && memberRows.some((member) => member.name === myName)
  // Distinct senders seen in this channel. `allSpeakers` is the UNFILTERED set -- kept separately
  // so the empty state can tell "nobody has said anything here" apart from "everyone who has is
  // currently hidden", exactly the distinction MessagesPage already makes for its own history.
  // Collapsing that into one "nobody yet" message would reintroduce the same empty-vs-filtered
  // ambiguity `visible-participants.ts` documents `conversation.participants` as having had.
  const indexItems = index.data?.items
  const allSpeakers = useMemo(
    () => Array.from(new Set((indexItems ?? []).map((item) => item.sender))),
    [indexItems],
  )
  // Built straight from `visibleMessages` (not the `visibleParticipants` wrapper) because
  // `ZoneMessageIndexEntry` already carries a `sender` field.
  const speakers = useMemo(
    () => Array.from(new Set(visibleMessages(indexItems ?? [], hidden).map((item) => item.sender))),
    [indexItems, hidden],
  )

  return (
    <div className="grid gap-6 border-t border-border bg-muted/30 p-4 md:grid-cols-2">
      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <UsersIcon className="size-3.5 text-primary" />
            Members
          </h3>
          <Button
            variant={isMember ? 'outline' : 'default'}
            size="sm"
            hidden={readOnly}
            disabled={!zone.reachable || joinChannel.isPending || leaveChannel.isPending}
            onClick={() => {
              if (isMember) {
                leaveChannel.mutate({ zoneId: zone.id, channelId: channel.id })
              } else {
                joinChannel.mutate({ zoneId: zone.id, id: channel.id })
              }
            }}
          >
            {isMember ? (
              <>
                <LogOutIcon className="size-3.5" />
                {leaveChannel.isPending ? 'Leaving…' : 'Leave'}
              </>
            ) : (
              <>
                <LogInIcon className="size-3.5" />
                {joinChannel.isPending ? 'Joining…' : 'Join'}
              </>
            )}
          </Button>
        </div>

        {members.isError ? (
          <ErrorState
            error={members.error}
            title="Could not load members"
            onRetry={() => {
              void members.refetch()
            }}
          />
        ) : members.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : memberRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nobody has joined this channel.</p>
        ) : (
          <ul className="space-y-1.5">
            {memberRows.map((member) => (
              <li
                key={member.name}
                className="flex items-center justify-between gap-2 rounded border border-border bg-card px-2.5 py-1.5 text-sm"
              >
                <span className="font-mono">{member.name}</span>
                <span className="flex items-center gap-2">
                  <Badge variant="outline">{member.connection_type}</Badge>
                  <span
                    className="text-xs text-muted-foreground"
                    title={formatAbsolute(member.joined_at)}
                  >
                    {formatRelative(member.joined_at)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <MessagesSquareIcon className="size-3.5 text-primary" />
            Conversations
          </h3>
          {/* Same destination as the channel name in the row above -- offered again here
              because this panel is where someone is already reading this channel's
              conversations and wants the messages themselves. */}
          <Link
            to={`/app/messages?channel=${channel.id}`}
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
          >
            <MessagesSquareIcon className="size-3.5" />
            Open in Messages
          </Link>
        </div>

        {/* The real, observable half of the shared visibility filter -- see this component's
            javadoc for why `conversation.participants` below cannot be it today. Three distinct
            states, not two: genuinely nobody, everybody hidden, or a real (filtered) list -- the
            middle one must not read the same as the first, or hiding everyone looks identical to
            an empty channel. */}
        <p data-speakers={speakers.join(',')} className="mb-2 text-xs text-muted-foreground">
          {allSpeakers.length === 0 ? (
            'Nobody has said anything in this channel yet.'
          ) : speakers.length === 0 ? (
            <>Every speaker here is hidden — see Agents on the Messages page.</>
          ) : (
            <>Speakers here: {speakers.join(', ')}</>
          )}
        </p>

        {conversations.isError ? (
          <ErrorState
            error={conversations.error}
            title="Could not load conversations"
            onRetry={() => {
              void conversations.refetch()
            }}
          />
        ) : conversations.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : conversationRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing has been said in this channel yet.</p>
        ) : (
          <ul aria-label="Channel conversations" className="space-y-1.5">
            {conversationRows.map((conversation) => {
              const shown = visibleParticipants(conversation.participants, hidden)
              const ongoing = conversation.ended_at === null
              return (
                <li
                  key={conversation.id}
                  className="flex items-center justify-between gap-2 rounded border border-border bg-card px-2.5 py-1.5 text-sm"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground">
                      {formatRelative(conversation.started_at)}
                      {ongoing ? ' · ongoing' : ` – ${formatRelative(conversation.ended_at as string)}`}
                    </span>
                    {/* Only renders once the backend actually populates a channel segment's
                        participants -- see this component's javadoc. Kept, not deleted, so the
                        wiring is already correct on the day that changes. */}
                    {shown.length > 0 ? (
                      <span
                        data-participants={shown.join(',')}
                        className="truncate font-mono text-xs text-muted-foreground"
                      >
                        {shown.join(', ')}
                      </span>
                    ) : null}
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {pluralise(conversation.message_count, 'message')}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

function ChannelsView({ zone }: { zone: ZoneSummary }) {
  const me = useMe()
  const isAdmin = me.data?.role === 'ADMIN'
  const presence = usePresence(zone.id)
  const channels = useChannels(zone.id)
  const create = useCreateChannel()
  const joinChannel = useJoinChannel()
  const removeChannel = useDeleteChannel()

  const [name, setName] = useState('')
  const [pendingTarget, setPendingTarget] = useState<ChannelSummary | undefined>(undefined)
  const [expandedId, setExpandedId] = useState<string | undefined>(undefined)

  const rows = channels.data ?? []
  const readOnly = useReadOnly()
  const writable = zone.reachable && !readOnly
  const myName = presence.data?.name
  // Same per-zone setting the Messages page reads (§ agent-visibility.ts) — never re-derived here,
  // so an agent hidden on one page cannot reappear because a channel row was opened on this one.
  // Deliberately NOT applied to `rows` above: the channels list itself must not look emptier than
  // it is just because an agent is hidden.
  const hidden = hiddenSenders(zone.id)
  const needsPresence =
    isMissingPresence(create.error) ||
    isMissingPresence(joinChannel.error) ||
    presence.data === null

  function submit(event: FormEvent) {
    event.preventDefault()
    const trimmed = name.trim()
    if (trimmed.length === 0) return
    create.mutate(
      { zoneId: zone.id, name: trimmed },
      {
        onSuccess: () => {
          setName('')
        },
      },
    )
  }

  return (
    <>
      <PageHeader
        eyebrow="Channels"
        title={`Channels in ${zone.name}`}
        description="Broadcast groups scoped to this zone. A message sent to a channel is queued once per joined connection, excluding the sender. Anything you create or join here is done as your human connection."
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void channels.refetch()
            }}
            disabled={channels.isFetching}
          >
            <RefreshCwIcon
              className={cn('size-4', channels.isFetching ? 'animate-spin' : undefined)}
            />
            Refresh
          </Button>
        }
      />

      <PageBody>
        <PausedZoneNotice zone={zone} />

        {needsPresence && writable ? (
          <MissingPresenceNotice zone={zone} action="Creating or joining a channel" />
        ) : null}

        <form onSubmit={submit} className="flex flex-wrap items-end gap-2" hidden={readOnly}>
          <Input
            value={name}
            onChange={(event) => {
              setName(event.target.value)
            }}
            placeholder="design-review"
            className="w-64 font-mono"
            autoComplete="off"
            disabled={!writable || create.isPending}
            aria-label="New channel name"
          />
          <Button type="submit" disabled={!writable || create.isPending || name.trim().length === 0}>
            <PlusIcon className="size-4" />
            {create.isPending ? 'Creating…' : 'Create channel'}
          </Button>
          <p className="text-sm text-muted-foreground">
            {channels.isLoading ? 'Loading…' : pluralise(rows.length, 'channel')} in this zone
          </p>
        </form>

        {create.isError && !isMissingPresence(create.error) ? (
          <Alert variant="destructive">
            <AlertTitle>Could not create that channel</AlertTitle>
            <AlertDescription><ApiErrorText error={create.error} /></AlertDescription>
          </Alert>
        ) : null}

        {/* Channels are polled every 15s, so a row can outlive the channel behind it:
            joining one another ADMIN just deleted is a 404 that nothing else reports. */}
        {joinChannel.isError && !isMissingPresence(joinChannel.error) ? (
          <Alert variant="destructive">
            <AlertTitle>Could not join that channel</AlertTitle>
            <AlertDescription><ApiErrorText error={joinChannel.error} /></AlertDescription>
          </Alert>
        ) : null}

        {removeChannel.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not delete that channel</AlertTitle>
            <AlertDescription>{errorMessage(removeChannel.error)}</AlertDescription>
          </Alert>
        ) : null}

        {channels.isError ? (
          <ErrorState
            error={channels.error}
            title="Could not load channels"
            onRetry={() => {
              void channels.refetch()
            }}
          />
        ) : channels.isLoading ? (
          <Card className="overflow-hidden">
            <TableSkeleton rows={3} columns={4} />
          </Card>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={HashIcon}
            title="No channels in this zone yet"
            description={
              <>
                Create one above, or let an agent do it from inside the zone with{' '}
                <code className="font-mono text-foreground">
                  create_channel(&quot;design-review&quot;)
                </code>
                . Whoever creates a channel joins it automatically; everyone else joins with{' '}
                <code className="font-mono text-foreground">join_channel</code>.
              </>
            }
          />
        ) : (
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10" />
                  <TableHead>Channel</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Created by</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((channel) => {
                  const expanded = expandedId === channel.id
                  return (
                    <Fragment key={channel.id}>
                      <TableRow>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={expanded ? 'Collapse channel details' : 'Expand channel details'}
                            aria-expanded={expanded}
                            data-testid="channel-expand-toggle"
                            data-channel-id={channel.id}
                            onClick={() => {
                              setExpandedId(expanded ? undefined : channel.id)
                            }}
                          >
                            {expanded ? (
                              <ChevronDownIcon className="size-3.5" />
                            ) : (
                              <ChevronRightIcon className="size-3.5" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {/* The channel name IS the jump. `?channel=` is the same URL parameter
                              the Messages page's filter already reads and writes (see
                              `ChannelFilterSelect`'s javadoc), so arriving here selects that
                              channel in the dropdown and narrows the history, the rail and the
                              CSV export together -- no new shared state, and the link is
                              copyable. */}
                          <Link
                            to={`/app/messages?channel=${channel.id}`}
                            title={`Show #${channel.name} on the Messages page`}
                            className="rounded font-mono text-sm font-medium text-foreground underline-offset-4 hover:text-primary hover:underline"
                          >
                            {channel.name}
                          </Link>
                          <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
                            {channel.id}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={channel.member_count === 0 ? 'secondary' : 'outline'}>
                            <span className="font-mono tabular">{channel.member_count}</span>
                            {channel.member_count === 1 ? 'member' : 'members'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm text-muted-foreground">
                          {channel.created_by_connection_name ?? '—'}
                        </TableCell>
                        <TableCell
                          className="text-sm whitespace-nowrap text-muted-foreground"
                          title={formatAbsolute(channel.created_at)}
                        >
                          {formatRelative(channel.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!writable || joinChannel.isPending}
                              onClick={() => {
                                joinChannel.mutate({ zoneId: zone.id, id: channel.id })
                              }}
                            >
                              <LogInIcon className="size-3.5" />
                              Join
                            </Button>
                            {isAdmin && !readOnly ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={removeChannel.isPending}
                                onClick={() => {
                                  setPendingTarget(channel)
                                }}
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <Trash2Icon className="size-3.5" />
                                Delete
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expanded ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={6} className="p-0">
                            <ChannelDetail zone={zone} channel={channel} myName={myName} hidden={hidden} />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </PageBody>

      <AlertDialog
        open={pendingTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingTarget(undefined)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete <span className="font-mono">{pendingTarget?.name ?? 'this channel'}</span>?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingTarget === undefined
                ? null
                : `${pluralise(pendingTarget.member_count, 'connection')} will be removed from it. `}
              Anything still queued from this channel is discarded and will never be delivered,
              though it stays in the zone history — messages are permanent. A channel with the
              same name can be created again afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeChannel.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive' }))}
              disabled={removeChannel.isPending}
              onClick={(event) => {
                event.preventDefault()
                const target = pendingTarget
                if (target === undefined) return
                removeChannel.mutate(
                  { zoneId: zone.id, id: target.id },
                  {
                    onSuccess: () => {
                      setPendingTarget(undefined)
                    },
                  },
                )
              }}
            >
              {removeChannel.isPending ? 'Deleting…' : 'Delete channel'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export function ChannelsPage() {
  return <RequireZone>{(zone) => <ChannelsView zone={zone} />}</RequireZone>
}
